import React, { useState, useEffect } from "react";
import "./addUser.css";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL || "https://api.transtrackglobal.in";

export const AddUser = () => {
  const initialState = {
    client: "",
    resourceName: "",
    recruiterName: "",
    gender: "",
    dob: "",
    email: "",
    mobile: "",
    dojTapl: "",
    dojE: "",
    domain: "",
    location: "",
    lineManager: "",
    lmEmail: "",
    lmMobile: "",
    jobStage: "",
    jobRole: "",
    rateCard: "",
    monthlyctc: "",
    ctc: "",
    lastWorkingDay: "",
    aadhar: "",
    pan: "",
    refName1: "",
    refName2: "",
    refMobile1: "",
    refMobile2: "",
    woNo: "",
    woRenewalDate: "",
  };

  const [user, setUser] = useState(initialState);
  const [domainOptions, setDomainOptions] = useState([]);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");

  const fetchDomains = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/domains`);
      setDomainOptions(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const inputHandler = (e) => {
    const { name, value } = e.target;

    // Aadhar: only digits
    if (name === "aadhar") {
      if (!/^\d*$/.test(value)) return;
    }

    // PAN: uppercase + alphanumeric
    if (name === "pan") {
      const upper = value.toUpperCase();
      if (!/^[A-Z0-9]*$/.test(upper)) return;

      setUser((prev) => ({
        ...prev,
        pan: upper,
      }));
      return;
    }

    // CTC → Monthly CTC auto calculation
    if (name === "ctc") {
      const monthly = value ? (Number(value) / 12).toFixed(2) : "";

      setUser((prev) => ({
        ...prev,
        ctc: value,
        monthlyctc: monthly,
      }));
      return;
    }

    // Default handler
    setUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/domains/add`, { name: newDomain });
      toast.success(res.data.message);
      setNewDomain("");
      setShowDomainModal(false);
      fetchDomains(); // Refresh domain list
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add domain");
    }
  };

  const getMissingFields = () => {
    const missing = [];
    if (!user.resourceName?.toString().trim()) missing.push("Resource Name");
    if (!user.client?.toString().trim()) missing.push("Client");
    if (!user.recruiterName?.toString().trim()) missing.push("Recruiter Name");
    if (!user.gender) missing.push("Gender");
    if (!user.email?.toString().trim()) missing.push("Email");
    if (!user.mobile?.toString().trim() || user.mobile?.toString().length !== 10) missing.push("Mobile (10 digits)");
    if (!user.domain) missing.push("Domain");
    if (!user.jobRole?.toString().trim()) missing.push("Job Role");
    if (user.rateCard === "" || user.rateCard === null || user.rateCard === undefined) missing.push("Rate Card");
    if (user.ctc === "" || user.ctc === null || user.ctc === undefined) missing.push("CTC");
    if (!user.jobStage) missing.push("Job Stage");
    return missing;
  };

  const isValidAadhar = (aadhar) => /^\d{12}$/.test(aadhar);
  const isValidPan = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.toUpperCase());
  const isValidPhone = (num) => /^\d{10}$/.test(num);

  const submitForm = async (e) => {
    e.preventDefault();

    if (user.aadhar && !isValidAadhar(user.aadhar)) {
      toast.error("Invalid Aadhar number. It must be 12 digits.");
      return;
    }

    if (user.pan && !isValidPan(user.pan)) {
      toast.error("Invalid PAN format.");
      return;
    }

    if (user.mobile && !isValidPhone(user.mobile)) {
      toast.error("Invalid Mobile number. It must be 10 digits.");
      return;
    }

    const missingFields = getMissingFields();
    if (missingFields.length > 0) {
      toast.error(`Please fill mandatory fields: ${missingFields.join(", ")}`);
      return;
    }

    if (user.lmMobile && !isValidPhone(user.lmMobile)) {
      toast.error("Invalid Line Manager Mobile number. It must be 10 digits.");
      return;
    }

    if (user.refMobile1 && !isValidPhone(user.refMobile1)) {
      toast.error("Invalid Reference 1 Mobile number. It must be 10 digits.");
      return;
    }

    if (user.refMobile2 && !isValidPhone(user.refMobile2)) {
      toast.error("Invalid Reference 2 Mobile number. It must be 10 digits.");
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/insertUserData`, user);
      toast.success(response.data.message, { position: "top-right" });
      navigate("/user");
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message, { position: "top-right" });
      } else {
        toast.error("An error occurred during submission");
      }
    }
  };

  return (
    <div className="add-user-container">
      <div className="add-user-card">
        <div className="add-user-header">
          <h4 className="mb-0 fw-semibold">
            <i className="bi bi-person-plus-fill me-2 text-primary"></i> Add Record
          </h4>
          <Link to="/user" className="btn outline-btn btn-sm text-nowrap">
            <i className="bi bi-arrow-left me-1"></i> Back to list
          </Link>
        </div>
        
        <form className="add-user-form" onSubmit={submitForm}>
          
          <div className="form-section">
            <h6 className="section-title">General Details</h6>
            <div className="form-grid">
              <div className="input-group-modern">
                <label htmlFor="client">Client <span className="text-danger">*</span></label>
                <input type="text" id="client" name="client" value={user.client} onChange={inputHandler} placeholder="Enter client name" autoComplete="off" />
              </div>
              
              <div className="input-group-modern">
                <label htmlFor="resourceName">Resource Name <span className="text-danger">*</span></label>
                <input type="text" id="resourceName" name="resourceName" value={user.resourceName} onChange={inputHandler} placeholder="Enter resource name" autoComplete="off" />
              </div>
              
              <div className="input-group-modern">
                <label htmlFor="recruiterName">Recruiter Name <span className="text-danger">*</span></label>
                <input type="text" id="recruiterName" name="recruiterName" value={user.recruiterName} onChange={inputHandler} placeholder="Enter recruiter name" autoComplete="off" />
              </div>
              
              <div className="input-group-modern">
                <label htmlFor="gender">Gender <span className="text-danger">*</span></label>
                <select id="gender" name="gender" value={user.gender} onChange={inputHandler}>
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="input-group-modern">
                <label htmlFor="dob">Date of Birth</label>
                <input type="date" id="dob" name="dob" value={user.dob} onChange={inputHandler} />
              </div>

              <div className="input-group-modern">
                <label htmlFor="email">Email ID <span className="text-danger">*</span></label>
                <input type="email" id="email" name="email" value={user.email} onChange={inputHandler} placeholder="Enter email" autoComplete="off" />
              </div>
              
              <div className="input-group-modern">
                <label htmlFor="mobile">Mobile No <span className="text-danger">*</span></label>
                <input type="tel" id="mobile" name="mobile" value={user.mobile} onChange={inputHandler} placeholder="Enter 10-digit mobile number" autoComplete="off" maxLength="10" minLength="10" />
                {user.mobile && !isValidPhone(user.mobile) && <small className="text-danger mt-1 d-block">Must be 10 digits</small>}
              </div>

              <div className="input-group-modern">
                <label htmlFor="aadhar">Aadhar Number</label>
                <input type="text" id="aadhar" name="aadhar" value={user.aadhar} onChange={inputHandler} placeholder="Enter 12-digit Aadhar" autoComplete="off" maxLength="12" minLength="12" />
                {user.aadhar && !isValidAadhar(user.aadhar) && <small className="text-danger mt-1 d-block">Must be 12 digits</small>}
              </div>

              <div className="input-group-modern">
                <label htmlFor="pan">PAN Number</label>
                <input type="text" id="pan" name="pan" value={user.pan} onChange={inputHandler} placeholder="Enter PAN number" autoComplete="off" maxLength="10" minLength="10" />
                {user.pan && !isValidPan(user.pan) && <small className="text-danger mt-1 d-block">Invalid PAN format (AAAAA9999A)</small>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h6 className="section-title">Employment Details</h6>
            <div className="form-grid">
              <div className="input-group-modern">
                <label htmlFor="dojTapl">DOJ TAPL</label>
                <input type="date" id="dojTapl" name="dojTapl" value={user.dojTapl} onChange={inputHandler} />
              </div>

              <div className="input-group-modern">
                <label htmlFor="dojE">DOJ Client</label>
                <input type="date" id="dojE" name="dojE" value={user.dojE} onChange={inputHandler} />
              </div>

              <div className="input-group-modern">
                <label htmlFor="domain">Domain <span className="text-danger">*</span></label>
                <div className="d-flex gap-2">
                  <select id="domain" name="domain" value={user.domain} onChange={inputHandler} className="flex-grow-1" style={{ width: '100%' }}>
                    <option value="">Select Domain</option>
                    {domainOptions.map((d) => (
                      <option key={d._id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                  {(userType === "admin" || userType === "superadmin") && (
                    <button type="button" onClick={() => setShowDomainModal(true)} className="btn outline-btn px-3" title="Add Domain">
                      <i className="bi bi-plus-lg"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="input-group-modern">
                <label htmlFor="location">Location</label>
                <input type="text" id="location" name="location" value={user.location} onChange={inputHandler} placeholder="Enter location" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="lineManager">Line Manager</label>
                <input type="text" id="lineManager" name="lineManager" value={user.lineManager} onChange={inputHandler} placeholder="Enter line manager" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="lmEmail">LM Email ID</label>
                <input type="email" id="lmEmail" name="lmEmail" value={user.lmEmail} onChange={inputHandler} placeholder="Enter line manager email" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="lmMobile">LM Mobile No</label>
                <input type="tel" id="lmMobile" name="lmMobile" value={user.lmMobile} onChange={inputHandler} placeholder="Enter line manager mobile" autoComplete="off" maxLength="10" />
                {user.lmMobile && !isValidPhone(user.lmMobile) && <small className="text-danger mt-1 d-block">Must be 10 digits</small>}
              </div>

              <div className="input-group-modern">
                <label htmlFor="jobStage">Job Stage <span className="text-danger">*</span></label>
                <select id="jobStage" name="jobStage" value={user.jobStage} onChange={inputHandler}>
                  <option value="">Select job stage</option>
                  <option value="Pipeline">Pipeline</option>
                  <option value="Shortlisted">Shortlisted</option>
                  <option value="Offered">Offered</option>
                  <option value="Joined">Joined</option>
                  <option value="Serving NP">Serving NP</option>
                  <option value="Released">Released</option>
                  <option value="Absconded">Absconded</option>
                  <option value="Dropout">Dropout</option>
                </select>
              </div>

              <div className="input-group-modern">
                <label htmlFor="jobRole">Job Role <span className="text-danger">*</span></label>
                <input type="text" id="jobRole" name="jobRole" value={user.jobRole} onChange={inputHandler} placeholder="Enter job role" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="lastWorkingDay">Last Working Day</label>
                <input type="date" id="lastWorkingDay" name="lastWorkingDay" value={user.lastWorkingDay} onChange={inputHandler} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h6 className="section-title">Financial Details</h6>
            <div className="form-grid">
              <div className="input-group-modern">
                <label htmlFor="rateCard">Rate Card <span className="text-danger">*</span></label>
                <input type="number" id="rateCard" name="rateCard" value={user.rateCard} onChange={inputHandler} placeholder="Enter rate card" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="ctc">CTC <span className="text-danger">*</span></label>
                <input type="number" id="ctc" name="ctc" value={user.ctc} onChange={inputHandler} placeholder="Enter CTC" autoComplete="off" />
              </div>
              
              <div className="input-group-modern">
                <label htmlFor="monthlyctc">Monthly CTC <span className="text-danger">*</span></label>
                <input type="number" id="monthlyctc" name="monthlyctc" value={user.monthlyctc} readOnly className="readonly-input" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h6 className="section-title">References & WO Details</h6>
            <div className="form-grid">
              <div className="input-group-modern">
                <label htmlFor="refName1">Ref-Name 1</label>
                <input type="text" id="refName1" name="refName1" value={user.refName1} onChange={inputHandler} placeholder="Enter reference 1 name" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="refMobile1">Ref-1 Mobile No</label>
                <input type="tel" id="refMobile1" name="refMobile1" value={user.refMobile1} onChange={inputHandler} placeholder="Enter reference 1 mobile" autoComplete="off" maxLength="10" minLength="10" />
                {user.refMobile1 && !isValidPhone(user.refMobile1) && <small className="text-danger mt-1 d-block">Must be 10 digits</small>}
              </div>

              <div className="input-group-modern">
                <label htmlFor="refName2">Ref-Name 2</label>
                <input type="text" id="refName2" name="refName2" value={user.refName2} onChange={inputHandler} placeholder="Enter reference 2 name" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="refMobile2">Ref-2 Mobile No</label>
                <input type="tel" id="refMobile2" name="refMobile2" value={user.refMobile2} onChange={inputHandler} placeholder="Enter reference 2 mobile" autoComplete="off" maxLength="10" minLength="10" />
                {user.refMobile2 && !isValidPhone(user.refMobile2) && <small className="text-danger mt-1 d-block">Must be 10 digits</small>}
              </div>

              <div className="input-group-modern">
                <label htmlFor="woNo">WO No</label>
                <input type="text" id="woNo" name="woNo" value={user.woNo} onChange={inputHandler} placeholder="Enter WO number" autoComplete="off" />
              </div>

              <div className="input-group-modern">
                <label htmlFor="woRenewalDate">WO Renewal Date</label>
                <input type="date" id="woRenewalDate" name="woRenewalDate" value={user.woRenewalDate} onChange={inputHandler} />
              </div>
            </div>
          </div>

          <div className="form-actions mt-4">
            {getMissingFields().length > 0 && (
              <span className="text-muted small me-3"><i className="bi bi-info-circle me-1"></i>Please fill all required fields (*)</span>
            )}
            <button type="submit" className="btn gradient-btn submit-btn-modern" disabled={getMissingFields().length > 0}>
              <i className="bi bi-send-fill me-2"></i> Submit Record
            </button>
          </div>

        </form>
      </div>

      {/* DOMAIN MODAL */}
      {showDomainModal && (
        <div className="modal-overlay-modern">
          <div className="modal-card-modern" style={{ maxWidth: '400px' }}>
            <div className="modal-header-modern">
              <h5 className="mb-0"><i className="bi bi-plus-circle me-2"></i> Add New Domain</h5>
              <button type="button" className="btn-close-modern" onClick={() => setShowDomainModal(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            <div className="p-4">
              <div className="input-group-modern mb-0">
                <label>Domain Name</label>
                <input type="text" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="e.g. Healthcare" />
              </div>
            </div>
            <div className="modal-footer-modern">
              <button type="button" className="btn outline-btn" onClick={() => setShowDomainModal(false)}>Cancel</button>
              <button type="button" className="btn gradient-btn" onClick={handleAddDomain}>Add Domain</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
